import { useState, useEffect, useRef, memo, useMemo, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import CustomButton from "../Buttons/CustomButton";
import CustomTooltip from "../Buttons/CustomTooltip";
import React from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import DbMigrationChart from "./DbMigrationChart";
import axios from "axios";

const styles = {
  scrollContainer: {
    height: "85vh",
    maxHeight: "100vh",
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  },
  verticleLine: {
    height: "100%",
    width: 1,
    backgroundColor: "#909090",
  },
};

const getStatusStyles = (status) => {
  switch (status.toLowerCase()) {
    case "failed":
      return {
        backgroundColor: "#FFD2D2",
        borderRight: "2px solid #EB5757",
      };
    case "in_progress":
      return {
        backgroundColor: "#B5C2EB",
        borderRight: "2px solid #8298DD",
      };
    case "completed":
      return {
        backgroundColor: "#D2FFDC",
        borderRight: "2px solid #5FCE2F",
      };
    default:
      return {
        backgroundColor: "#ddd",
        borderRight: "2px solid #ccc",
      };
  }
};

const RecentActivities = memo(
  ({ title, activities, loading, icon, noActivitiesMessage, itemLabel }) => {
    const truncateText = useCallback((text, maxLength) => {
      if (!text) return "N/A";

      const isTruncated = text.length > maxLength;
      const truncated = isTruncated
        ? `${text.substring(0, maxLength)}...`
        : text;

      return isTruncated ? (
        <CustomTooltip
          content={text}
          placement="right"
          fallbackPlacements={["top"]}
        >
          <span className="truncated-text" style={{ cursor: "pointer" }}>
            {truncated}
          </span>
        </CustomTooltip>
      ) : (
        <span>{text}</span>
      );
    }, []);

    const validActivities = useMemo(() => {
      const filtered = activities?.filter((activity) => activity?.job_name) || [];

      // Sort by timestamp (assuming completed_at is a sortable timestamp)
      const sorted = filtered.sort((a, b) => 
        new Date(b.completed_at) - new Date(a.completed_at)
      );

      return sorted.slice(0, 5); // Take newest 5
    }, [activities]);

    const formattedActivities = useMemo(() => {
      return validActivities.map((activity) => ({
        ...activity,
        completed_at: activity.completed_at || "N/A",
        num_tables_migrated: activity.total_tables || 0,
      }));
    }, [validActivities]);

    const showEmptyState = useMemo(
      () => loading || !validActivities || validActivities.length === 0,
      [loading, validActivities]
    );

    return (
      <div className="card card-migration h-100 w-100 mb-0 cursor-pointer">
        <div
          className="card-body p-3 d-flex flex-column"
          style={{ height: "100%" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="d-flex align-items-center">
              <span className="rounded-circle prof me-2">
                <img
                  src={icon || "/placeholder.svg"}
                  style={{ height: "19px", width: "19px", marginBottom: "4px" }}
                  alt={title}
                />
              </span>
              <h6 className="dbcardheadsdesign mb-0">{title}</h6>
            </div>

            {formattedActivities.length > 0 && (
              <h5
                className="card-title fw-bold mb-0"
                style={{ fontSize: "11px", textDecoration: "underline" }}
              >
                <a
                  href="/listmigration"
                  style={{ color: "inherit", textDecoration: "inherit" }}
                >
                  View All
                </a>
              </h5>
            )}
          </div>

          <div
            className={`flex-grow-1 d-flex flex-column ${
              showEmptyState
                ? "align-items-center justify-content-center"
                : ""
            }`}
            style={{
              minHeight: "297px",
              overflowY: "auto",
            }}
          >
            {showEmptyState ? (
              <div className="d-flex flex-column align-items-center justify-content-center h-100 w-100">
                <img
                  src="assets/images/emptystate.gif"
                  style={{ 
                    width: "180px", 
                    opacity: 0.7, 
                    marginBottom: "0px",
                    transition: "opacity 0.3s ease" // Smooth transition
                  }}
                  alt="No activities found"
                />
                <p className="text-muted mb-0" style={{ transition: "opacity 0.3s ease" }}>
                  {loading ? "Loading activities..." : noActivitiesMessage}
                </p>
              </div>
            ) : (
              <div style={{ width: "100%" }}>
                {formattedActivities.map((activity, idx, arr) => (
                  <div
                    key={`${activity.job_id || idx}`}
                    className="d-flex"
                    style={{ marginBottom: "20px" }}
                  >
                    <div
                      style={{
                        width: "40px",
                        position: "relative",
                        top: "5px",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#E3F2FD",
                          borderRadius: "50%",
                          width: "25px",
                          height: "25px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src="/assets/images/databases.jpg"
                          alt="conversion"
                          width="25px"
                          height="25px"
                        />
                      </div>
                      {idx !== arr.length - 1 && (
                        <div
                          style={{
                            position: "absolute",
                            top: "25px",
                            bottom: "-20px",
                            left: "10.5px",
                            width: "2px",
                            backgroundColor: "#ECEAEE",
                          }}
                        />
                      )}
                    </div>

                    <div
                      style={{
                        flex: 1,
                        paddingLeft: "0px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        marginTop: "5px",
                      }}
                    >
                      <div
                        className="d-flex align-items-center"
                        style={{
                          justifyContent: "space-between",
                          gap: "10px",
                          alignItems: "center",
                        }}
                      >
                        <strong
                          style={{
                            fontFamily: "Inter",
                            fontWeight: 500,
                            fontSize: "13px",
                            color: "#202B3E",
                            maxWidth: "110px",
                            minWidth: "110px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {truncateText(activity.job_name, 10)}
                        </strong>

                        <div
                          className="d-flex align-items-center text-muted"
                          style={{
                            fontSize: "12px",
                            minWidth: "135px",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                        >
                          <i
                            className="ri-calendar-2-line"
                            style={{ fontSize: "14px", marginRight: "4px" }}
                          ></i>
                          {activity.completed_at}
                        </div>
                      </div>

                      <small
                        className="text-muted"
                        style={{ fontSize: "12px" }}
                      >
                        Migrated {activity.num_tables_migrated} tables
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

function SummaryDBMigration({
  setShow,
  setCurrentStep,
  activeTab,
  setActiveTab,
  profile,
}) {
  const location = useLocation();
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { state } = useAuth();
  const handleStartMigration = () => {
    navigate("/listmigration", {
      state: {
        openCreateDatabase: true,
        databaseStep: 1,
      },
    });
  };
  const socketRef = useRef(null);
  const retryDelayRef = useRef(1000);
  const maxRetryDelay = 16000;
  const socketRef1 = useRef(null);
  const retryDelayRef1 = useRef(1000);
  const maxRetryDelay1 = 16000;
  const [loading, setLoading] = useState(true);
  const job_id = location.state?.jobId;
  const [reportData, setReportData] = useState(null);

  const [chartData, setChartData] = useState(null);
  const [migrationData, setMigrationData] = useState({
    active: { count: "000", jobs: [] },
    completed: { count: "000", jobs: [] },
    upcoming: { count: "000", jobs: [] },
  });

  const handleCheckError = (migrationId) => {
    console.log(`Checking error for migration ${migrationId}`);
  };

  const allCountsZero = Object.values(migrationData).every(
    (item) => item.count === "000"
  );

  // State for all API data
  const [dashboardData, setDashboardData] = useState({
    recentlyCompletedActivities: [],
    monthlyBarChart: [],
    weeklyLineChart: [],
    cardMetrics: {
      tables_migrated: 0,
      running_migrations: 0,
      last_migration: "",
      failed_migrations: 0,
      databases: 0,
      total_jobs: 0,
      completed_jobs: 0,
      failed_jobs: 0,
      total_volume:0,
    },
    migrationProgress: {
      recentMigrations: [],
      overall_progress: 0,
    },
    jobs_by_skills: [],
    data_source_trends: [],
    recent_activities: [],
    recently_completed: {
    count: "000",
    jobs_difference: 0,
    percentage_change: 0,
    change_status: "no_change",
    jobs: []
  },
    active_migrations: [],
      migration_throughput: {
    average_migration_throughput: [],
    completed_migrations: []
  }
  });

  useEffect(() => {
    const connectWebSocket = () => {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) {
        console.error("Authentication token missing");
        setLoading(false);
        return;
      }
      const wsBaseReport = process.env.REACT_APP_API_URL_DATA_MIGRATION;
      socketRef1.current = new WebSocket(
        `${wsBaseReport}/ws/user-reports?token=${authToken}`
      );

      socketRef1.current.onopen = () => {
        console.log("User reports WebSocket connected");
        setError(null);
        setLoading(false);
        retryDelayRef1.current = 1000;
      };

     socketRef1.current.onmessage = (event) => {
  try {
    const message = JSON.parse(event.data);
    if (message.event === "user_reports_update") {
      const data = message.data;

      setDashboardData({
        recentlyCompletedActivities: data.recently_completed || [],
        monthlyBarChart: data.monthly_bar_chart || [],
        weeklyLineChart: data.weekly_line_chart || [],
        cardMetrics: {
          tables_migrated: data.card_metrics?.tables_migrated || 0,
          running_migrations: data.card_metrics?.running_migrations || 0,
          last_migration: data.card_metrics?.last_migration || "N/A",
          failed_migrations: data.card_metrics?.failed_migrations || 0,
          databases: data.card_metrics?.databases || 0,
          total_jobs: data.card_metrics?.total_jobs || 0,
          total_volume: data.card_metrics?.total_volume || 0,
          completed_jobs: data.card_metrics?.completed_jobs || 0,
          failed_jobs: data.card_metrics?.failed_jobs || 0,
        },
        migrationProgress: {
          recentMigrations: data.recent_activities || [],
          overall_progress: 0,
        },
        jobs_by_skills: data.jobs_by_skills || [],
        data_source_trends: data.data_source_trends || [],
        recent_activities: data.recent_activities || [],
    recently_completed: data.recently_completed || {
          count: "000",
          jobs_difference: 0,
          percentage_change: 0,
          change_status: "no_change",
          jobs: []
        },
        active_migrations: data.active_migrations || [],
        migration_throughput: data.migration_throughput || {
          average_migration_throughput: [],
          completed_migrations: []
        }
      });
    }
  } catch (err) {
    console.error("WebSocket message parsing error:", err);
  }
};

      socketRef1.current.onclose = (event) => {
        console.warn("User reports WebSocket closed:", event);
        if (!document.hidden) {
          if (retryDelayRef1.current >= maxRetryDelay1) {
            setError("Connection lost. Retrying...");
          }
          setTimeout(connectWebSocket, retryDelayRef1.current);
          retryDelayRef1.current = Math.min(
            retryDelayRef1.current * 2,
            maxRetryDelay1
          );
        }
      };

      socketRef1.current.onerror = (err) => {
        console.error("WebSocket error:", err);
        socketRef1.current?.close();
      };
    };

    const handleVisibilityChange = () => {
      if (document.hidden && socketRef1.current) {
        socketRef1.current.close();
      } else if (
        !document.hidden &&
        (!socketRef1.current ||
          socketRef1.current.readyState === WebSocket.CLOSED)
      ) {
        connectWebSocket();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    connectWebSocket();

    return () => {
      if (socketRef1.current) socketRef1.current.close();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const DatabasesCard = memo(({ jobStatus, loading }) => {
    return (
      <div
        className="card border-0 card-migration mb-1"
        style={{ borderRadius: "8px", minHeight: "124px" }}
      >
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="d-flex align-items-center">
                
                <div className="icons-bg me-2 font-size-36">
                  <i className="dash-icon-database"></i>
                </div>
                <h5
                  className="card-title fw-bold mb-0 dbcardsdesign"
                  style={{
                    height: "35px", 
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  Total Volume of DB
                </h5>
              </div>
              {loading ? (
                <div className="spinner-border spinner-border-sm text-secondary"></div>
              ) : (
                <h4 className="mb-2 mt-2 homecardfont">{jobStatus ?? "0"}</h4>
              )}
              <h6
                className="text-muted mb-1"
                style={{ fontSize: "12px", whiteSpace: "nowrap" }}
              >
                Total storage used across DBs
              </h6>
            </div>
          </div>
        </div>
      </div>
    );
  });

  const TablesmigratedCard = ({ total_files, loading }) => (
    <div
      className="card border-0 card-migration mb-1"
      style={{ borderRadius: "8px", minHeight: "124px" }}
    >
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center">
              <div className="icons-bg me-2">
                  <i className="dash-icon-database-migrated"></i>
              </div>
              <h5
                className="card-title fw-bold mb-0 dbcardsdesign"
                style={{
                  height: "35px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Databases Migrated
              </h5>
            </div>
            {loading ? (
              <div className="spinner-border spinner-border-sm text-secondary"></div>
            ) : (
              <h4 className=" mb-2 mt-2 homecardfont">{total_files ?? "0"}</h4>
            )}
            <h6 className="text-muted mb-1" style={{ fontSize: "12px" }}>
              Combined source & target DBs
            </h6>
          </div>
        </div>
      </div>
    </div>
  );

  const RunningmigrationsCard = ({ running_migrations, loading }) => (
    <div
      className="card border-0 card-migration mb-1"
      style={{ borderRadius: "8px", minHeight: "124px" }}
    >
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center">
                
              <div className="icons-bg me-2">
                  <i className="dash-icon-table-migrated"></i>
              </div>
             
              <h5
                className="card-title fw-bold mb-0 dbcardsdesign"
                style={{
                  height: "35px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Table Migrated
              </h5>
            </div>
            {loading ? (
              <div className="spinner-border spinner-border-sm text-secondary"></div>
            ) : (
              <h4 className=" mb-2 mt-2 homecardfont">
                {running_migrations ?? "0"}
              </h4>
            )}
            <h6 className="text-muted mb-1" style={{ fontSize: "12px" }}>
              Tables successfully migrated
            </h6>
          </div>
        </div>
      </div>
    </div>
  );

  const LastmigrationCard = ({
    last_migration,
    loading,
    success = 2,
    failed = 1,
  }) => (
    <div
      className="card border-0 card-migration mb-1"
      style={{ borderRadius: "8px", minHeight: "124px" }}
    >
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center">
              <div className="icons-bg me-2">
                  <i className="dash-icon-total-jobs"></i>
              </div>
              <h5
                className="card-title fw-bold mb-0 dbcardsdesign"
                style={{
                  height: "35px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Total Migration Jobs
              </h5>
            </div>
            {loading ? (
              <div className="spinner-border spinner-border-sm text-secondary"></div>
            ) : (
              <div className="d-flex align-items-center justify-content-between mt-2 mb-2 flex-wrap">
                <div className="homecardfont display-6 fw-bold">
                  {last_migration ? (
                    last_migration.length > 10 ? (
                      <CustomTooltip
                        content={last_migration}
                        placement="top"
                        fallbackPlacements={["bottom"]}
                      >
                        <span>
                          {last_migration
                            .replace(
                              /\w\S*/g,
                              (txt) =>
                                txt.charAt(0).toUpperCase() +
                                txt.substr(1).toLowerCase()
                            )
                            .substring(0, 10)}
                          ...
                        </span>
                      </CustomTooltip>
                    ) : (
                      last_migration.replace(
                        /\w\S*/g,
                        (txt) =>
                          txt.charAt(0).toUpperCase() +
                          txt.substr(1).toLowerCase()
                      )
                    )
                  ) : (
                    "N/A"
                  )}
                </div>

                <div className="d-flex align-items-center gap-3">
                  <div className="text-success migration-success-count text-center">
                    <div>{`00${success}`}</div>
                    <small className="d-block">Success</small>
                  </div>
                  <div className="text-danger migration-failed-count text-center">
                    <div>{`00${failed}`}</div>
                    <small className="d-block">Failed</small>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <h6 className="text-muted mb-1" style={{ fontSize: "12px" }}>
          Number of migration tasks created
        </h6>
      </div>
    </div>
  );

 const StatusChart = React.memo(
  ({ title, icon, jobsBySkill = [], loading }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const chartContainerRef = useRef(null);
    const codeMigrationColors = [
      { backgroundColor: "#C0DAFF", borderColor: "#5C84BD" },
      { backgroundColor: "#B5EBD0", borderColor: "#4AAC7B" },
      { backgroundColor: "#FFE9D2", borderColor: "#D39D62" },
    ];

    // Auto-collapse if data shrinks to <= 2 items
    // useEffect(() => {
    //   if (jobsBySkill.length <= 2 && isExpanded) {
    //     setIsExpanded(false);
    //   }
    // }, [jobsBySkill.length, isExpanded]);

    const toggleExpand = useCallback(() => {
      if (!document.fullscreenElement) {
        chartContainerRef.current
          ?.requestFullscreen()
          .then(() => {
            setIsExpanded(true);
          })
          .catch((err) => {
            console.error('Error entering fullscreen:', err);
            setIsExpanded(false);
          });
      } else {
        document
          .exitFullscreen()
          .then(() => {
            setIsExpanded(false);
          })
          .catch((err) => {
            console.error('Error exiting fullscreen:', err);
          });
      }
    }, []);

    useEffect(() => {
      const handleFullscreenChange = () => {
        const isFullscreen = !!document.fullscreenElement;
        const isOurElement = document.fullscreenElement === chartContainerRef.current;
        
        // Only update state if it's our element that changed fullscreen state
        if (isFullscreen !== isExpanded) {
          setIsExpanded(isFullscreen && isOurElement);
        }
      };

      document.addEventListener("fullscreenchange", handleFullscreenChange);
      
      return () => {
        document.removeEventListener("fullscreenchange", handleFullscreenChange);
      };
    }, [isExpanded]);

    // Handle case where component unmounts while in fullscreen
    useEffect(() => {
      return () => {
        if (document.fullscreenElement === chartContainerRef.current) {
          document.exitFullscreen().catch(() => {});
        }
      };
    }, []);

    const showEmptyState = useMemo(
      () => loading || !jobsBySkill || jobsBySkill.length === 0,
      [loading, jobsBySkill]
    );

    const skillItems = useMemo(() => {
      const maxJobs = Math.max(...jobsBySkill.map((s) => s.jobs), 1);
      return jobsBySkill.map((skill) => ({
        source: skill.source,
        target: skill.target,
        count: skill.jobs,
        percentage: (skill.jobs / maxJobs) * 100,
      }));
    }, [jobsBySkill]);

    // Determine if we should show minimize/maximize controls
    const showMinMaxControls = jobsBySkill.length > 2;

    return (
      <div 
        className="card card-migration min-h-100p mb-0 cursor-pointer"
        ref={chartContainerRef}
        style={isExpanded ? { 
          height: 'calc(100vh - 100px)',
          backgroundColor: '#fff', // Ensure background is white in fullscreen
          zIndex: 1000 // Higher z-index in fullscreen
        } : {}}
      >
        <div className="card-body p-3 d-flex flex-column">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="d-flex align-items-center dbcardheadsdesign mb-0">
              <div className="icons-bg me-2">
                <span className="dash-icon-by-skills">
                  <span className="path1"></span>
                  <span className="path2"></span>
                  <span className="path3"></span>
                </span>
              </div>
              <span>{title}</span>
            </h6>
            
            {showMinMaxControls && (
              <CustomTooltip
                content={isExpanded ? "Exit fullscreen" : "Enter fullscreen"}
                placement="left"
              >
                <i
                  className={`mdi ${
                    isExpanded ? "mdi-fullscreen-exit" : "mdi-fullscreen"
                  } text-dark font-size-22 fullscreen`}
                  style={{ cursor: "pointer" }}
                  onClick={toggleExpand}
                />
              </CustomTooltip>
            )}
          </div>

          {!showEmptyState && (
            <p className="text-muted mb-2" style={{ fontSize: "12px" }}>
              Number of conversion jobs by source-to-target DB pairs
            </p>
          )}

          <div className="flex-grow-1 d-flex flex-column">
            {showEmptyState ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ height: "200px" }}
              >
                <img
                  src="assets/images/emptystate.gif"
                  style={{ width: "200px", opacity: 1 }}
                  alt="No Data Available"
                />
                <p className="text-muted mt-2">No skills data available</p>
              </div>
            ) : (
              <div 
                className="status-bars"
                style={{ 
                  flex: 1,
                  overflowY: isExpanded ? 'auto' : 'visible'
                }}
              >
                {(isExpanded ? skillItems : skillItems.slice(0, 2)).map((item, index) => {
                  const color = codeMigrationColors[index % codeMigrationColors.length];
                  return (
                    <div
                      className="status"
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "12px",
                      }}
                    >
                      <div
                        className="label stat-text"
                        style={{
                          fontWeight: "400",
                          fontFamily: "inter",
                          fontSize: "13px",
                          minWidth: "90px",
                          maxWidth: "100px",
                          display: "flex",
                          flexDirection: "column",
                          lineHeight: "1.3",
                          marginRight: "6px",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span>{item.source}</span>
                          <span style={{ margin: "0 4px" }}>➜</span>
                        </div>
                        <div>{item.target}</div>
                      </div>

                      <div
                        className="bar-container"
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <div
                          className="bar"
                          style={{
                            height: "36px",
                            width: `${item.percentage * 0.8}%`,
                            backgroundColor: color.backgroundColor,
                            borderRight: `2px solid ${color.borderColor}`,
                            borderRadius: "4px",
                            position: "relative",
                          }}
                        ></div>

                        <span
                          className="jobskillcolor"
                          style={{
                            marginLeft: "8px",
                            fontWeight: "500",
                            fontSize: "13px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.count}
                        </span>
                      </div>
                    </div>
                  );
                })}
                
                {!isExpanded && skillItems.length > 2 && (
                  <div className="text-center mt-2">
                    <small className="text-muted">
                      +{skillItems.length - 2} more items
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

  return (
    <div
      className="container-fluid"
      style={{ display: "flex", flexDirection: "column", gap: "8px",marginTop:"-6px",
         padding:"0px 1.5px 0px 1.5px",
       }}
    >
      {/* First Row: 5 cards in a row */}

      <div className="row g-2 mt-1 ">
        <div className="col">
          <DatabasesCard
            jobStatus={dashboardData?.cardMetrics?.total_volume}
            loading={loading}
          />
        </div>
        <div className="col">
          <TablesmigratedCard
            total_files={dashboardData?.cardMetrics?.databases}
            loading={loading}
          />
        </div>
        <div className="col">
          <RunningmigrationsCard
            running_migrations={dashboardData?.cardMetrics?.tables_migrated}
            loading={loading}
          />
        </div>
        <div className="col">
          <LastmigrationCard
            last_migration={dashboardData?.cardMetrics?.total_jobs?.toString()}
            loading={loading}
            success={dashboardData?.cardMetrics?.completed_jobs}
            failed={dashboardData?.cardMetrics?.failed_jobs}
          />
        </div>
      </div>

      {/* Second Row: 3 cards in a row */}
      <div className="row g-2">
        {/* Active Migrations */}
      
<div className="col-12 col-sm-6 col-md-3 col-lg-3 mb-1">
  <div className="card card-migration min-h-100p mb-0 cursor-pointer">
    <div className="card-body p-3">
      <h6 className="d-flex align-items-center dbcardheadsdesign">
        <div className="icons-bg me-2">
          <i className="dash-icon-act-migration"></i>
        </div>
        <span>Active Migration</span>
      </h6>

     {loading || !dashboardData.active_migrations || dashboardData.active_migrations.length === 0 ? (
        <div className="d-flex justify-content-center" style={{flexDirection:"column",textAlign:"center",alignItems:"center"}}>
          <img
            src="assets/images/emptystate.gif"
            style={{ width: "200px" }}
            alt="No Data Available"
          />
          <p className="text-muted mt-2">No data available</p>
        </div>
      ) : (
        <div>
          <h4 className="pt-0 mb-0 text-d-secondary homecardfont">
            {dashboardData.active_migrations.length}
          </h4>

          {/* Sort by started_at and take only the latest 2 */}
          {dashboardData.active_migrations
            .sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
            .slice(0, 2)
            .map((job, idx) => (
              <div key={idx}>
                <h6 className="font-size-12 mt-2 pt-0 mb-0 text-d-black">
                  {job.job_name}
                </h6>
                <div className="progress mt-1 mb-1">
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${job.progress_percentage}%` }}
                    aria-valuenow={job.progress_percentage}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                <p
                  className="font-size-11 text-d-mins"
                  style={{ fontWeight: "100" }}
                >
                  {job.progress_percentage}% - {job.tables_migrated}
                </p>
              </div>
            ))}
          
          {/* Show message if there are more than 2 active migrations */}
          {/* {dashboardData.active_migrations.length > 2 && (
            <p className="text-muted font-size-11 mt-2">
              +{dashboardData.active_migrations.length - 2} more active migrations
            </p>
          )} */}
        </div>
      )}
    </div>
  </div>
</div>

        
      {/* Recently Completed Migrations */}
<div className="col-12 col-sm-6 col-md-3 col-lg-3 mb-1">
  <div className="card card-migration min-h-100p mb-0 cursor-pointer">
    <div className="card-body p-3">
      <h6 className="d-flex align-items-center dbcardheadsdesign">
         <div className="icons-bg me-2">
                  <i className="dash-icon-recently-completed" style={{fontSize : "26px"}}></i>
              </div>
        
        <span>Recently Completed</span>
      </h6>
{loading || !dashboardData.recently_completed || !dashboardData.recently_completed.jobs || dashboardData.recently_completed.jobs.length === 0 ? (
        <div className="d-flex justify-content-center" style={{flexDirection:"column",textAlign:"center",alignItems:"center"}}>
          <img
            src="assets/images/emptystate.gif"
            style={{ width: "200px" }}
            alt="No Data Available"
          />
          <p className="text-muted mt-2">No data available</p>
        </div>
      ) : (
        <div>
          <h4 className="pt-0 mb-0 text-d-secondary homecardfont">
            {dashboardData.recently_completed.count}
          </h4>

          {dashboardData.recently_completed.jobs.length > 0 && (
            <h6 className="pt-2 font-size-12 mb-3 text-d-primary">
              <span
                className={`text-d-number me-1 ${
                  dashboardData.recently_completed.change_status === "increase" 
                    ? "text-success" 
                    : dashboardData.recently_completed.change_status === "decrease" 
                      ? "text-danger" 
                      : "text-muted"
                }`}
                style={{ fontWeight: "600" }}
              >
                <i className={`fas ${
                  dashboardData.recently_completed.change_status === "increase" 
                    ? "fa-arrow-up" 
                    : dashboardData.recently_completed.change_status === "decrease" 
                      ? "fa-arrow-down" 
                      : ""
                } font-size-10`}></i> 
                ({dashboardData.recently_completed.percentage_change}%)
              </span>
              {dashboardData.recently_completed.jobs_difference} {
                dashboardData.recently_completed.change_status === "increase" 
                  ? "Increased" 
                  : dashboardData.recently_completed.change_status === "decrease" 
                    ? "Decreased" 
                    : "No change"
              }
            </h6>
          )}

          {dashboardData.recently_completed.jobs.map((job, idx) => (
            <div key={idx}>
              <h6 className="font-size-12 pt-0 mb-0 text-d-black">
                {job.job_name}
              </h6>
              <div className="d-flex justify-content-between pt-2">
                <p
                  className="font-size-11 text-d-mins"
                  style={{ fontWeight: "100" }}
                >
                  Completed at {job.completed_at || "N/A"}
                </p>
                <p
                  className="font-size-11 text-d-rate"
                  style={{
                    fontWeight: "600",
                    whiteSpace: "nowrap",
                  }}
                >
                  Tables: {job.num_tables_migrated || 0}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
</div>

     


{/* Migration Throughput Card */}
<div className="col-12 col-sm-6 col-md-3 col-lg-3 mb-1">
  <div className="card card-migration min-h-100p mb-0 cursor-pointer">
    <div className="card-body p-3">
      <h6 className="d-flex align-items-center dbcardheadsdesign">
        <div className="icons-bg me-2">
                  <i className="dash-icon-act-migration"></i>
              </div>
        <span>Migration Throughput</span>
      </h6>

      {loading || !dashboardData.migration_throughput || 
 (dashboardData.migration_throughput.average_migration_throughput === "0.00 MB/s" && 
  (!dashboardData.migration_throughput.completed_migrations || 
   dashboardData.migration_throughput.completed_migrations.length === 0)) ? (
        <div className="d-flex justify-content-center" style={{flexDirection:"column",textAlign:"center",alignItems:"center"}}>
          <img
            src="assets/images/emptystate.gif"
            style={{ width: "200px" }}
            alt="No Data Available"
          />
          <p className="text-muted mt-2">No data available</p>
        </div>
      ) : (
        <div>
          {/* Only show average throughput if it's not "0.00 MB/s" */}
          {dashboardData.migration_throughput.average_migration_throughput && 
           dashboardData.migration_throughput.average_migration_throughput !== "0.00 MB/s" && (
            <h4 className="pt-0 mb-0 text-d-secondary homecardfont">
              {dashboardData.migration_throughput.average_migration_throughput}
            </h4>
          )}
        
          {/* Completed Migrations */}
          {dashboardData.migration_throughput.completed_migrations?.length > 0 ? (
            <>
              {dashboardData.migration_throughput.completed_migrations.map((migration, idx) => (
                <div key={migration.job_id || idx}>
                  <h6 className="font-size-12 mt-2 pt-0 mb-0 text-d-black">
                    {migration.job_name}
                  </h6>
                  <div className="progress mt-1 mb-1">
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: "100%" }}
                      aria-valuenow={100}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                  <p
                    className="font-size-11 text-d-mins"
                    style={{ fontWeight: "100" }}
                  >
                    Migration Throughput: {migration.throughput}
                  </p>
                </div>
              ))}
            </>
          ) : (
            <div className="d-flex justify-content-center" style={{flexDirection:"column",textAlign:"center",alignItems:"center"}}>
              <img
                src="assets/images/emptystate.gif"
                style={{ width: "200px" }}
                alt="No Data Available"
              />
              <p className="text-muted mt-2">No data available</p>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
</div>

        <div className="col-12 col-sm-6 col-md-3 col-lg-3 mb-1">
          <StatusChart
            title="Jobs by Skills"
            icon="assets/images/skillmigration.png"
            loading={loading}
            jobsBySkill={dashboardData.jobs_by_skills}
          />
        </div>
      </div>

      {/* Third Row: Recent Activities and Migration Over Time side by side */}

      <div className="row g-2" style={{ minHeight: "auto" ,marginTop:"-11px"}}>
        <div className="col-12 mb-0 ">
          <div className="border-0" style={{ borderRadius: "8px" }}>
            <DbMigrationChart
            
 
            
            />
          </div>
        </div>
        {/* Left: Data Source Trends */}

    {/* Data Source Trends Card */}
  
<div className="col-lg-6 col-md-6" style={{marginTop:"7px"}}>
  <div className="card card-migration" style={{ height: "97.1%", minHeight: "300px" }}>
    <div className="card-body p-3 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="d-flex align-items-center dbcardheadsdesign mb-0">
         <div className="icons-bg me-2">
                  <i className="dash-icon-data-source"></i>
              </div>
          <span>Data Source Trends</span>
        </h6>
      </div>
      
    {dashboardData.data_source_trends && dashboardData.data_source_trends.length > 0 && (
        <p className="text-muted font-size-11 mb-2 mt-1">
          Number of migration jobs by source-to-target DB pairs
        </p>
      )}

      <div className="flex-grow-1 d-flex flex-column" style={{ overflowY: 'auto' }}>
        {dashboardData.data_source_trends.length > 0 ? (
          <div style={{ flex: 1 }}>
            {dashboardData.data_source_trends.map((item, idx, arr) => (
              <div
                key={idx}
                className="d-flex"
                style={{ marginBottom: "20px" }}
              >
                <div style={{ width: "40px", position: "relative" }}>
                  <div class="icons-bg3 me-2"><i class="dash-icon-database"></i></div>
                  {idx !== arr.length - 1 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "25px",
                        bottom: "-20px",
                        left: "10.5px",
                        width: "2px",
                        backgroundColor: "#ECEAEE",
                      }}
                    />
                  )}
                </div>

                <div
                  style={{
                    flex: 1,
                    paddingLeft: "0px",
                    marginTop: "5px",
                  }}
                >
                  <div className="d-flex stat-text2 align-items-center"
                    style={{
                      justifyContent: "space-between",
                      gap: "10px",
                    }}
                  >
                    <strong
                      style={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "13px",
                        maxWidth: "300px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.source_db}
                      {item.target_db ? ` → ${item.target_db}` : ""}
                    </strong>

                    <span class ="trendscss">
                      {item.total_completed_jobs}
                    </span>
                  </div>

                  <small className="text-muted" style={{ fontSize: "12px" }}>
                    {item.total_completed_tables} tables migrated
                  </small>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 w-100">
            <img
              src="assets/images/emptystate.gif"
              style={{
                width: "180px",
                opacity: 0.7,
                marginBottom: "10px",
              }}
              alt="No trends found"
            />
            <p className="text-muted">
              {loading ? "Loading data..." : "No data source trends available"}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
</div>

{/* Recent Activities Card */}
<div className="col-lg-6 col-md-6" style={{marginTop:"7px"}}>
  <div className="card card-migration" style={{ height: "97.1%", minHeight: "300px" }}>
    <div className="card-body p-3 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div className="d-flex align-items-center">
         <div className="icons-bg me-2">
                  <i className="dash-icon-recent-code"></i>
              </div>
          <h6 className="dbcardheadsdesign mb-0 ps-1 ms-1">Recent Activities</h6>
        </div>
        {dashboardData.recent_activities && dashboardData.recent_activities.length > 0 && (
          <h5 style={{ fontSize: "11px", textDecoration: "underline" }}>
            <a href="/listmigration" style={{ color: "inherit", textDecoration: "inherit" }}>
              View All
            </a>
          </h5>
        )}
      </div>

      <div className="flex-grow-1 d-flex flex-column mt-2" style={{ overflowY: 'auto' }}>
        {dashboardData.recent_activities && dashboardData.recent_activities.length > 0 ? (
          <div style={{ flex: 1 }}>
            {dashboardData.recent_activities
              .slice(0, 5) // Only take the first 5 items
              .map((activity, idx, arr) => (
                <div key={`${activity.job_id || idx}`} className="d-flex" style={{ marginBottom: "20px" }}>
                <div style={{ width: "40px", position: "relative", top: "5px" }}>
                 <div class="icons-bg3 me-2"><i class="dash-icon-database"></i></div>
                  {idx !== arr.length - 1 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "25px",
                        bottom: "-20px",
                        left: "10.5px",
                        width: "2px",
                        backgroundColor: "#ECEAEE",
                      }}
                    />
                  )}
                </div>

                <div style={{ 
                  flex: 1, 
                  paddingLeft: "0px", 
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  marginTop: "5px"
                }}>
                  <div className="d-flex align-items-center"
                    style={{
                      justifyContent: "space-between",
                      gap: "10px",
                      alignItems: "center",
                    }}
                  >
                    <strong className="stat-text2"
                      style={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: "13px",
                        maxWidth: "110px",
                        minWidth: "110px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {activity.job_name}
                    </strong>

                    <div className="d-flex align-items-center text-muted"
                      style={{
                        fontSize: "12px",
                        minWidth: "135px",
                        justifyContent: "flex-end",
                         marginLeft: "auto", 
                        alignItems: "center",
                      }}
                    >
                      <i className="ri-calendar-2-line" style={{ fontSize: "14px", marginRight: "4px" }}></i>
                      {activity.completed_at}
                    </div>
                  </div>

                  <small className="text-muted" style={{ fontSize: "12px" }}>
                    Migrated {activity.num_tables_migrated} tables
                  </small>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 w-100">
            <img
              src="assets/images/emptystate.gif"
              style={{ 
                width: "180px", 
                opacity: 0.7,
                marginBottom: "10px"
              }}
              alt="No activities found"
            />
            <p className="text-muted">
              {loading ? "Loading activities..." : "No recent activities yet"}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
</div>
      </div>
    </div>
    
  );
}

export default SummaryDBMigration;
